'use client';

import React, { useState } from 'react';
import { Plus, X, Scale } from 'lucide-react';
import { FLOCK_GROWTH_SUMMARIES } from './growthData';

interface Props {
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

const FLOCKS = FLOCK_GROWTH_SUMMARIES;

export default function WeightSampleForm({ onSave, onClose }: Props) {
  const [form, setForm] = useState({
    flockId: FLOCKS[0]?.flockId ?? '',
    recordDate: new Date().toISOString().split('T')[0],
    ageDays: '',
    numberSampled: '',
    individualWeightsRaw: '', // comma-separated kg values
    feedConsumedKg: '',
    cumulativeFeedKg: '',
    recordedBy: 'Okello James',
    notes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedFlock = FLOCKS.find(f => f.flockId === form.flockId);

  function parseWeights(raw: string): number[] {
    return raw
      .split(/[\s,]+/)
      .map(v => parseFloat(v.trim()))
      .filter(v => !isNaN(v) && v > 0);
  }

  const parsedWeights = parseWeights(form.individualWeightsRaw);
  const avgWeight = parsedWeights.length > 0
    ? parsedWeights.reduce((s, w) => s + w, 0) / parsedWeights.length
    : null;
  const minWeight = parsedWeights.length > 0 ? Math.min(...parsedWeights) : null;
  const maxWeight = parsedWeights.length > 0 ? Math.max(...parsedWeights) : null;
  const withinRange = avgWeight
    ? parsedWeights.filter(w => Math.abs(w - avgWeight) / avgWeight <= 0.10).length
    : 0;
  const uniformityPct = parsedWeights.length > 0
    ? Math.round((withinRange / parsedWeights.length) * 100)
    : null;

  // Std deviation
  const stdDev = avgWeight && parsedWeights.length > 1
    ? Math.sqrt(parsedWeights.reduce((s, w) => s + Math.pow(w - avgWeight, 2), 0) / parsedWeights.length)
    : null;

  function validate() {
    const e: Record<string, string> = {};
    if (!form.flockId) e.flockId = 'Select a flock';
    if (!form.recordDate) e.recordDate = 'Required';
    if (!form.ageDays || isNaN(Number(form.ageDays))) e.ageDays = 'Enter valid age in days';
    if (!form.numberSampled || isNaN(Number(form.numberSampled))) e.numberSampled = 'Enter number sampled';
    if (parsedWeights.length === 0) e.individualWeightsRaw = 'Enter at least one weight';
    if (!form.feedConsumedKg || isNaN(Number(form.feedConsumedKg))) e.feedConsumedKg = 'Enter feed consumed';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    const flock = FLOCKS.find(f => f.flockId === form.flockId);
    onSave({
      flockId: form.flockId,
      flockNumber: flock?.flockNumber,
      flockName: flock?.flockName,
      recordDate: form.recordDate,
      ageDays: Number(form.ageDays),
      numberSampled: Number(form.numberSampled),
      individualWeights: parsedWeights,
      avgWeightKg: avgWeight,
      minWeightKg: minWeight,
      maxWeightKg: maxWeight,
      stdDeviation: stdDev,
      uniformityPct,
      feedConsumedKg: Number(form.feedConsumedKg),
      cumulativeFeedKg: Number(form.cumulativeFeedKg),
      recordedBy: form.recordedBy,
      notes: form.notes,
    });
  }

  function field(key: keyof typeof form, label: string, type = 'text', placeholder = '') {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
        <input
          type={type}
          value={form[key]}
          onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
          placeholder={placeholder}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors[key] ? 'border-red-400' : 'border-gray-200'}`}
        />
        {errors[key] && <p className="text-xs text-red-500 mt-0.5">{errors[key]}</p>}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <Scale size={18} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Record Weight Sample</h2>
              <p className="text-xs text-gray-400">Enter individual bird weights for the flock</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Flock Selection */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Flock</label>
            <select
              value={form.flockId}
              onChange={e => setForm(p => ({ ...p, flockId: e.target.value }))}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.flockId ? 'border-red-400' : 'border-gray-200'}`}
            >
              {FLOCKS.map(f => (
                <option key={f.flockId} value={f.flockId}>
                  {f.flockNumber} — {f.flockName} ({f.farm})
                </option>
              ))}
            </select>
            {selectedFlock && (
              <p className="text-xs text-gray-400 mt-1">
                {selectedFlock.breed} · {selectedFlock.birdType} · Current age: {selectedFlock.currentAgeDays} days
              </p>
            )}
          </div>

          {/* Date + Age */}
          <div className="grid grid-cols-2 gap-4">
            {field('recordDate', 'Record Date', 'date')}
            {field('ageDays', 'Age (Days)', 'number', 'e.g. 21')}
          </div>

          {/* Number Sampled */}
          {field('numberSampled', 'Number of Birds Sampled', 'number', 'e.g. 50')}

          {/* Individual Weights */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Individual Weights (kg) — comma or space separated
            </label>
            <textarea
              value={form.individualWeightsRaw}
              onChange={e => setForm(p => ({ ...p, individualWeightsRaw: e.target.value }))}
              placeholder="e.g. 1.24, 1.31, 1.18, 1.42, 1.29 ..."
              rows={3}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${errors.individualWeightsRaw ? 'border-red-400' : 'border-gray-200'}`}
            />
            {errors.individualWeightsRaw && <p className="text-xs text-red-500 mt-0.5">{errors.individualWeightsRaw}</p>}
          </div>

          {/* Live Calculations */}
          {parsedWeights.length > 0 && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="text-center">
                <p className="text-xs text-blue-500 font-medium">Avg Weight</p>
                <p className="text-base font-bold text-blue-800">{avgWeight?.toFixed(3)} kg</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-blue-500 font-medium">Min / Max</p>
                <p className="text-base font-bold text-blue-800">{minWeight?.toFixed(3)} / {maxWeight?.toFixed(3)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-blue-500 font-medium">Uniformity</p>
                <p className={`text-base font-bold ${(uniformityPct ?? 0) >= 80 ? 'text-green-700' : 'text-amber-700'}`}>
                  {uniformityPct}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-blue-500 font-medium">Std Dev</p>
                <p className="text-base font-bold text-blue-800">{stdDev?.toFixed(3)} kg</p>
              </div>
            </div>
          )}

          {/* Feed */}
          <div className="grid grid-cols-2 gap-4">
            {field('feedConsumedKg', 'Feed Consumed This Period (kg)', 'number', 'e.g. 204.4')}
            {field('cumulativeFeedKg', 'Cumulative Feed to Date (kg)', 'number', 'e.g. 388.4')}
          </div>

          {/* Recorded By + Notes */}
          <div className="grid grid-cols-2 gap-4">
            {field('recordedBy', 'Recorded By')}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <input
                type="text"
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Optional observations"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
            >
              <Plus size={15} />
              Save Record
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
